import {loadConsentPreferences} from "./ConsentPreferencesApp";
import {loadConsentRequest} from "./ConsentRequestApp";
import {loadConsentRequiredApp} from "./ConsentRequiredApp";

class ConsentManager {
    constructor() {
        console.log('Start ConsentManager.js');

        this.consentRequestApp = null;
        this.consentPreferencesApp = null;
        this.consentRequiredApp = null;

        // Define an array to store consent listeners
        this.consentListeners = [];

        /**
         * Define all supported consent types and the default value:
         *
         * ad_storage: Enables storage, such as cookies, related to advertising.
         * ad_user_data: Sets consent for sending user data to Google for online advertising purposes.
         * ad_personalization: Sets consent for personalized advertising.
         * analytics_storage: Enables storage, such as cookies, related to analytics (for example, visit duration).
         * functionality_storage: Enables storage that supports the functionality of the website or app such as language settings.
         * personalization_storage: Enables storage related to personalization such as video recommendations.
         * security_storage: Enables storage related to security such as authentication functionality, fraud prevention, and other user protection
         *
         * @type {{ad_storage: string, ad_user_data: string, security_storage: string, personalization_storage: string, functionality_storage: string, ad_personalization: string, analytics_storage: string}}
         */
        this.consentTypes = {
            functionality_storage: 'granted',
            security_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            personalization_storage: 'denied',
        };

        this.requiredConsentTypes = ['functionality_storage', 'security_storage'];

        // Bind the onConsentChange method to the instance to ensure 'this' refers to the class instance
        this.onConsentChange = this.onConsentChange.bind(this);

        // Automatically attempt to load existing consent settings
        this.requestConsent();

        // Register the consent link listeners
        this.registerLinkListeners();
    }

    registerListener(callback) {
        console.log('Registering consent listener');

        this.consentListeners.push(callback);
    }

    registerLinkListeners() {
        const consentPreferencesLink = document.querySelector('.consent-preferences-link');
        const consentRequestLink = document.querySelector('.consent-request-link');

        if (consentPreferencesLink) {
            consentPreferencesLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.showConsentPreferences();
            });
        }

        if (consentRequestLink) {
            consentRequestLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.showConsentRequest();
            });
        }
    }

    /**
     *
     * @param consent {{ad_storage: string, ad_user_data: string, security_storage: string, personalization_storage: string, functionality_storage: string, ad_personalization: string, analytics_storage: string}}
     */
    onConsentChange(consent) {
        console.log('User consent changed:', consent);

        let consentPreferences = this.consentTypes;

        // Override the default settings with the consent values
        for (const key in consent) {
            if (consent.hasOwnProperty(key)) {
                consentPreferences[key] = consent[key];
            }
        }

        // Ensure the required consent types are set to 'granted'
        this.requiredConsentTypes.forEach(type => {
            if(consentPreferences[type] === 'granted') {
                return;
            }

            console.warn(`Required consent type "${type}" is not granted. Setting to "granted"`);
            consentPreferences[type] = 'granted';
        });

        this.consentListeners.forEach(callback => {
            callback(consentPreferences);
        });

        this.setConsentInLocalStorage(consentPreferences);
    }

    setConsentInLocalStorage(consent) {
        console.log('Setting consent storage with:', consent);

        localStorage.setItem('gifty_consent_settings', JSON.stringify(consent));
    }

    getConsentSettings() {
        const consentStorage = localStorage.getItem('gifty_consent_settings');
        let settings = this.consentTypes;

        // Expand and override the default settings with the stored settings
        if (consentStorage) {
            const storedSettings = JSON.parse(consentStorage);
            settings = {...settings, ...storedSettings};
        }

        return settings;
    }

    hasSavedPreferences() {
        return localStorage.getItem('gifty_consent_settings') !== null;
    }

    requestConsent() {
        const consentPreferencesSet = localStorage.getItem('gifty_consent_settings');

        if (consentPreferencesSet) {
            return
        }

        this.showConsentRequest();
    }

    showConsentRequest() {
        // Dynamically import the consent request app if it is not already loaded
        if(this.consentRequestApp === null) {
            this.consentRequestApp = loadConsentRequest()
        }

        this.consentRequestApp.show();
    }

    showConsentPreferences() {
        // Dynamically import the preferences app if it is not already loaded
        if(this.consentPreferencesApp === null) {
            this.consentPreferencesApp = loadConsentPreferences()
        }

        this.consentPreferencesApp.show();
    }

    /**
     * This method is called in the application to check if required consent types
     * are granted before continuing with certain functionality. If the required
     * consent types are not granted, showConsentRequired() is called.
     *
     * The method is asynchronous and will return a promise that resolves when
     * the user has provided consent.
     *
     * @param types
     */
    async requireConsentToContinue(types) {
        return new Promise((resolve, reject) => {
            const consentSettings = this.getConsentSettings();

            // Check if all required consent types are granted
            for (const type of types) {
                if (consentSettings[type] === 'granted') {
                    continue;
                }

                // Missing consent type, show the consent required modal
                this.showConsentRequired(types);

                // Register a listener to resolve the promise when the consent is granted
                const listener = (consent) => {
                    if (consent[type] === 'granted') {
                        resolve();

                        // Delete the listener once it has been resolved
                        this.consentListeners = this.consentListeners.filter(item => item !== listener);
                    }
                };

                this.registerListener(listener);

                return;
            }

            return resolve();
        });
    }


    /**
     * Similar to requireConsentToContinue(), but this method does not show the consent required modal.
     * Instead, it silently requires consent and resolves the promise when the consent is granted.
     * @param types
     * @returns {Promise<void>}
     */
    async silentlyRequireConsentToContinue(types) {
        return new Promise((resolve, reject) => {
            const consentSettings = this.getConsentSettings();

            // Check if all required consent types are granted
            for (const type of types) {
                if (consentSettings[type] === 'granted') {
                    continue;
                }

                // Register a listener to resolve the promise when the consent is granted
                const listener = (consent) => {
                    if (consent[type] === 'granted') {
                        resolve();

                        // Delete the listener once it has been resolved
                        this.consentListeners = this.consentListeners.filter(item => item !== listener);
                    }
                };

                this.registerListener(listener);

                return;
            }

            return resolve();
        });
    }

    /**
     * The consent required app shows the user a message that consent is required
     * when certain functionality is not available without consent. The modal
     * allows to user to still provide consent.
     */
    showConsentRequired(types) {
        // Dynamically import the requirement app if it is not already loaded
        if(this.consentRequiredApp === null) {
            this.consentRequiredApp = loadConsentRequiredApp()
        }

        this.consentRequiredApp.show(types);
    }
}

// Export an instance of ConsentManager
const consentManager = new ConsentManager();

// Expose registerListener and onConsentChange methods globally for compatibility
window.registerGTMConsentListener = consentManager.registerListener.bind(consentManager);

export default consentManager;
